import * as React from "react"

/**
 * Public-view AEGIS points at map layers, not the hidden model canvas.
 *
 * `linkableIds === null` means the regular editor: every `#id` links to a
 * model node. A set means public view, and only those Map layer node ids
 * become links. `highlightedNodeId` is the Map layer node currently hovered
 * from chat; the map hoists that layer and the layers list outlines it.
 */
type Listener = () => void

type MapOutputHighlightState = {
  linkableIds: Set<number> | null
  highlightedNodeId: number | null
}

let state: MapOutputHighlightState = {
  linkableIds: null,
  highlightedNodeId: null,
}

let activateLayer: ((layerId: number) => void) | null = null

/** Public view: a named intro mention can select a project layer that is not a Map layer node. */
export function setPublicLayerActivator(activate: ((layerId: number) => void) | null): void {
  activateLayer = activate
}

export function activatePublicLayer(layerId: number): void {
  activateLayer?.(layerId)
}

const listeners = new Set<Listener>()

function notify(): void {
  listeners.forEach(listener => {
    try {
      listener()
    } catch (_err) {
      // One subscriber must not block the rest.
    }
  })
}

function subscribe(callback: Listener): () => void {
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
  }
}

export function getMapOutputHighlightState(): MapOutputHighlightState {
  return state
}

/** Pass `null` to leave public-view linking (every `#id` targets the model). */
export function setPublicMapOutputLinks(ids: Set<number> | null): void {
  const highlightedNodeId =
    ids != null && state.highlightedNodeId != null && !ids.has(state.highlightedNodeId)
      ? null
      : state.highlightedNodeId
  if (state.linkableIds === ids && state.highlightedNodeId === highlightedNodeId) return
  state = { linkableIds: ids, highlightedNodeId }
  notify()
}

export function setMapOutputHighlight(nodeId: number | null): void {
  if (state.highlightedNodeId === nodeId) return
  state = { ...state, highlightedNodeId: nodeId }
  notify()
}

export function subscribeMapOutputHighlight(callback: Listener): () => void {
  return subscribe(callback)
}

/** React 17 subscriber. Re-renders when links or the hovered output change. */
export function useMapOutputHighlightSnapshot(): MapOutputHighlightState {
  const [snapshot, setSnapshot] = React.useState(getMapOutputHighlightState)
  React.useEffect(() => subscribeMapOutputHighlight(() => {
    setSnapshot(getMapOutputHighlightState())
  }), [])
  return snapshot
}
